"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import Image from "next/image";

const SearchBar = dynamic(() => import('./SearchBar'), {
  ssr: false,
  loading: () => <div className="w-full h-[54px] bg-white/20 backdrop-blur-md rounded-full shadow-sm" />
});
import { usePathname } from "next/navigation";
const HeroSectionMobile = dynamic(
  () => import("../../mobile/homepage/HeroSectionMobile")
);
import {
  destinationsToHeroChips,
  listPublishedDestinations,
} from "@/platforms/customer/services/destinationService";

import { getImageProps } from "next/image";

const HeroSection = ({ heroRef, interacted }) => {
  const pathname = usePathname();
  const isLandingPage = pathname === "/";
  const [items, setItems] = useState([]);
  const scrollContainerRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftStart = useRef(0);

  useEffect(() => {
    if (!interacted) return;
    let cancelled = false;
    
    (async () => {
      try {
        const countries = await listPublishedDestinations();
        if (!cancelled) setItems(destinationsToHeroChips(countries));
      } catch {
        if (!cancelled) setItems([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [interacted]);

  // Convert vertical mouse wheel into horizontal scroll
  const handleWheel = (e) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    }
  };

  const handleMouseDown = (e) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isDragging.current = true;
    el.classList.add("cursor-grabbing");
    startX.current = e.pageX - el.offsetLeft;
    scrollLeftStart.current = el.scrollLeft;
  };

  const handleMouseLeaveOrUp = () => {
    isDragging.current = false;
    scrollContainerRef.current?.classList.remove("cursor-grabbing");
  };

  const handleMouseMove = (e) => {
    const el = scrollContainerRef.current;
    if (!isDragging.current || !el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - startX.current;
    el.scrollLeft = scrollLeftStart.current - walk;
  };

  const { props: desktopBg } = getImageProps({
    src: "/images/hero-img.webp",
    alt: "Hero Background",
    fill: true,
    sizes: "100vw",
    quality: 75,
    priority: true,
  });

  const { props: mobileBg } = getImageProps({
    src: "/images/hero-section-img-mobile.png",
    alt: "Hero Background",
    fill: true,
    sizes: "100vw",
    quality: 75,
    priority: true,
  });

  return (
    <section id="hero" ref={heroRef} className="relative w-full overflow-hidden">
      {/* ── COMMON RESPONSIVE BACKGROUND ── */}
      {/* Absolute wrapper matching mobile (50vh) & desktop (75vh) heights via CSS */}
      <div className="absolute inset-0 z-0 h-[50vh] lg:h-[75vh]">
        <picture>
          <source media="(min-width: 1024px)" srcSet={desktopBg.srcSet} />
          <img
            {...mobileBg}
            loading="eager"
            fetchPriority="high"
            className="object-cover absolute inset-0 w-full h-full"
          />
        </picture>
      </div>

      {/* ── MOBILE FOREGROUND ── */}
      <div className="relative z-10 lg:hidden">
        <HeroSectionMobile />
      </div>

      {/* ── DESKTOP FOREGROUND ── */}
      <div className="relative z-10 hidden lg:block">
        <div className="relative min-h-[75vh] h-[75vh] w-full">
          <div className="absolute inset-0 z-10 flex items-center ta-desktop-home-container pt-28 lg:pt-32">
            <div className="flex w-full flex-col items-stretch ta-desktop-hero-stack">
              <div className="mb-6 w-full max-w-xl">
                {isLandingPage && <SearchBar animated={true} />}
              </div>
              <h1 className="font-(family-name:--font-oswald) hero-heading leading-none text-white uppercase">
                Meet, Voyage, Stories.
              </h1>
            </div>
          </div>
        </div>

        {items.length > 0 ? (
          <section className="ta-desktop-home-container min-h-[150px]">
            <div className="border border-[#D0D0D0FA] max-w-5xl mx-auto rounded-2xl bg-white/18 backdrop-blur-md -mt-10 min-h-[100px]">
              <div
                ref={scrollContainerRef}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeaveOrUp}
                onMouseUp={handleMouseLeaveOrUp}
                onMouseMove={handleMouseMove}
                className="flex flex-nowrap items-start justify-center gap-2 px-10 py-3 w-full mx-auto overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide cursor-grab select-none"
              >
                {items.map((item) => (
                  <Link
                    href={item.link}
                    key={item.id}
                    className="flex flex-col items-center cursor-pointer snap-start px-3"
                    style={{
                      flex: `0 0 calc((100% - ${(items.length - 1)} * 0.5rem) / ${Math.min(items.length, 6)})`,
                      maxWidth: `calc((100% - ${(items.length - 1)} * 0.5rem) / ${Math.min(items.length, 6)})`,
                    }}
                    onClick={(e) => {
                      if (isDragging.current) e.preventDefault();
                    }}
                    draggable={false}
                  >
                    <div className="relative w-full aspect-[7/8] rounded-2xl overflow-hidden border pointer-events-none">
                      <Image
                        src={item.image}
                        alt={item.name}
                        fill
                        className="w-full h-full object-cover object-center scale-105"
                        sizes="(max-width: 768px) 250px, 350px"
                      />
                    </div>
                    <p className="mt-2 text-[clamp(0.7rem,1.3vw,1rem)] font-medium text-black text-center truncate w-full pointer-events-none">
                      {item.name}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ) : null}
      </div>
    </section>
  );
};

export default HeroSection;
